import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpResponse
} from '@angular/common/http';

import {
  EMPTY,
  MonoTypeOperatorFunction,
  Observable,
  UnaryFunction,
  of,
  pipe,
  throwError,
  timer
} from 'rxjs';
import { RetryConfig, catchError, filter, map, tap } from 'rxjs/operators';

import { getRemoteError, RemoteError } from '../utils/get-remote-error.utils';

/**
 * Opções para o operador catchErrorWithRemoteMessage
 */
export interface CatchErrorWithRemoteMessageOptions<T = unknown> {
  /**
   * Se true, lança o erro ao invés de suprimir
   */
  rethrow?: boolean;
  /**
   * Valor de fallback retornado em caso de erro
   */
  fallbackValue?: T;
  /**
   * Mensagem padrão para erro remoto
   */
  defaultMessage?: string;
}

export function filterResponse<T>(): UnaryFunction<
  Observable<HttpEvent<T>>,
  Observable<T>
> {
  return pipe(
    filter((event: HttpEvent<T>) => event.type === HttpEventType.Response),
    map((response: HttpResponse<T>) => response.body)
  );
}

export function uploadProgress<T>(
  cb: (progress: number) => void
): MonoTypeOperatorFunction<HttpEvent<T>> {
  return tap((event: HttpEvent<T>) => {
    if (event.type === HttpEventType.UploadProgress) {
      cb(Math.round((event.loaded * 100) / event.total));
    }
  });
}

/**
 * RxJS operator to handle remote errors, optionally notifying via callback and returning a fallback value.
 *
 * @template T Type of the observable value.
 * @param onError Optional callback to notify about the remote error. Receives a RemoteError object with details (message, status, severity, etc).
 * @param options Options to control fallback value and error propagation:
 *   - rethrow: If true, rethrows the error instead of suppressing it (default: false).
 *   - fallbackValue: Value to emit in case of error (default: EMPTY).
 * @returns MonoTypeOperatorFunction<T> Returns an RxJS operator to be used in a pipe.
 *
 * @example
 *   source$.pipe(
 *     catchErrorWithRemoteMessage((err) => toast.danger(err.message), { fallbackValue: [] })
 *   )
 */
/**
 * RxJS operator to handle remote errors, optionally notifying via callback and returning a fallback value.
 *
 * @template T Type of the observable value.
 * @param onError Optional callback to notify about the remote error. Receives a RemoteError object with details (message, status, severity, etc).
 * @param options Options to control fallback value and error propagation:
 *   - rethrow: If true, rethrows the error instead of suppressing it (default: false).
 *   - fallbackValue: Value to emit in case of error (default: EMPTY).
 * @returns MonoTypeOperatorFunction<T> Returns an RxJS operator to be used in a pipe.
 *
 * @example
 *   source$.pipe(
 *     catchRemoteError((err) => toast.danger(err.message), { fallbackValue: [] })
 *   )
 */
/**
 * RxJS operator to handle remote errors, optionally notifying via callback and returning a fallback value.
 *
 * @template T Type of the observable value.
 * @param onError Optional callback to notify about the remote error. Receives a RemoteError object with details (message, status, severity, etc).
 * @param defaultMessage Optional default message to use if remote error does not provide one.
 * @param options Options to control fallback value and error propagation:
 *   - rethrow: If true, rethrows the error instead of suppressing it (default: false).
 *   - fallbackValue: Value to emit in case of error (default: EMPTY).
 * @returns MonoTypeOperatorFunction<T> Returns an RxJS operator to be used in a pipe.
 *
 * @example
 *   source$.pipe(
 *     catchRemoteError((err) => toast.danger(err.message), 'Default error', { fallbackValue: [] })
 *   )
 */
export function catchRemoteError<T>(
  onError?: (error: RemoteError) => void,
  options: CatchErrorWithRemoteMessageOptions<T> = {}
): MonoTypeOperatorFunction<T> {
  return pipe(
    catchError((error) => {
      const remoteError = getRemoteError(error, options.defaultMessage);
      if (onError) {
        onError(remoteError);
      }

      if (options.rethrow) {
        return throwError(() => error);
      }

      return options.fallbackValue !== undefined
        ? of(options.fallbackValue)
        : EMPTY;
    })
  );
}

export function shouldRetry({
  maxRetryAttempts = 3,
  scalingDuration = 1000,
  excludedStatusCodes = []
}: GenericRetryStrategy = {}): RetryConfig {
  return {
    count: maxRetryAttempts,
    delay(error, retryAttempt) {
      const err = error as HttpErrorResponse;
      if (
        retryAttempt > maxRetryAttempts ||
        excludedStatusCodes.some((statusCode) => statusCode === err.status)
      ) {
        return throwError(() => err);
      }
      return timer(retryAttempt * scalingDuration);
    }
  } as RetryConfig;
}

interface GenericRetryStrategy {
  maxRetryAttempts?: number;
  scalingDuration?: number;
  excludedStatusCodes?: number[];
}
